// チャット関連の型定義

export enum UserType {
  PARENT = "PARENT",
  STAFF = "STAFF",
}

export enum ChatType {
  DIRECT = "DIRECT",
  GROUP = "GROUP",
}

export type ConversationKind = "LEGACY" | "OFFICIAL_INBOX" | "COACH_DIRECT";

export type ChatWorkflowStatus =
  | "UNHANDLED"
  | "IN_PROGRESS"
  | "WAITING_PARENT"
  | "RESOLVED";

export type VisibleSenderType = "PERSON" | "FACILITY";

export type ChatMessageType = "TEXT" | "IMAGE" | "FILE";

export interface ChatIdentity {
  id: string;
  name: string;
  nameKana?: string | null;
  email?: string | null;
}

export interface ChatMemberCourse {
  id: string;
  name: string;
}

export interface ChatMemberStudent extends ChatIdentity {
  memberNumber?: number | null;
  courses: ChatMemberCourse[];
}

export interface FacilityChatIdentity extends ChatIdentity {
  organizationName?: string | null;
  logoUrl?: string | null;
}

export interface CoachDirectOption {
  parentId: string;
  parentName: string;
  parentEmail: string;
  studentId: string;
  studentName: string;
  coach: ChatIdentity;
}

export interface OfficialInboxParentOption {
  parentId: string;
  parentName: string;
  parentEmail: string;
  students: ChatIdentity[];
}

export interface ChatMessage {
  id: string;
  chatRoomId: string;
  senderId: string;
  senderType: UserType;
  content: string;
  createdAt: string;
  // 新契約移行中は optional。未返却時は senderType から従来表示へフォールバックする。
  visibleSenderType?: VisibleSenderType;
  visibleSenderName?: string | null;
  // 管理画面だけで表示する実送信者。保護者画面の公開名義には利用しない。
  actorName?: string | null;
  // 添付メッセージは公開URLを保持せず、閲覧時に期限付きURLを取得する。
  messageType?: ChatMessageType;
  attachmentId?: string | null;
  fileName?: string | null;
  fileSize?: number | null;
  mimeType?: string | null;
  attachmentExpiresAt?: string | null;
  reactions?: ChatMessageReaction[]; // 絵文字リアクション一覧（APIから取得）
}

export interface ChatAttachmentUploadTicket {
  attachmentId: string;
  uploadUrl: string;
  headers: Record<string, string>;
  uploadExpiresAt: string;
  maxFileSizeBytes: number;
}

export interface ChatAttachmentDownloadUrl {
  downloadUrl: string;
  expiresAt: string;
}

export interface UserChatRoom {
  userId: string;
  userType: UserType;
  lastReadAt?: string | null;
  isSelf?: boolean;
  name?: string | null;
  email?: string | null;
}

export interface ChatRoom {
  id: string;
  name?: string | null;
  displayName?: string | null;
  type: ChatType;
  conversationKind?: ConversationKind;
  // 施設スコープ。legacy データは null。
  facilityId?: string | null;
  facilityIdentity?: FacilityChatIdentity | null;
  officialParent?: ChatIdentity | null;
  // コーチ個別チャットの相談対象。複数受講者間の誤返信防止のためIDごと保持する。
  subjectStudent?: ChatIdentity | null;
  // 会員検索用。公式受信箱はその教室に通う受講者、コーチ個別は相談対象の受講者を返す。
  memberStudents?: ChatMemberStudent[];
  workflowStatus?: ChatWorkflowStatus | null;
  assignedStaffId?: string | null;
  assignedStaff?: ChatIdentity | null;
  workflowVersion?: number;
  userChatRooms: UserChatRoom[];
  currentUser?: UserChatRoom | null;
  otherParticipants?: UserChatRoom[];
  lastMessage?: ChatMessage | null;
  unreadCount: number;
  /** 通知ベル用の未読数。業務上の未読数は unreadCount に保持する。 */
  notificationUnreadCount?: number;
  updatedAt: string;
}

export interface ChatRoomActivity {
  roomId: string;
  lastMessage: ChatMessage;
  updatedAt: string;
  // 公式受信箱は送受信に連動して業務状態も変わるため、同じイベントで同期する。
  workflowStatus?: ChatWorkflowStatus | null;
  workflowVersion?: number;
  assignedStaffId?: string | null;
  assignedStaff?: ChatIdentity | null;
}

export interface ChatRoomWorkflowActivity {
  roomId: string;
  workflowStatus: ChatWorkflowStatus | null;
  workflowVersion: number;
  assignedStaffId: string | null;
  assignedStaff: ChatIdentity | null;
}

export interface ChatRoomReadEvent {
  roomId: string;
  userId: string;
  userType: UserType;
  readAt: string;
}

export interface SendMessageDto {
  roomId: string;
  senderId: string;
  content: string;
}

export interface CreateChatRoomDto {
  userIds: string[];
  name?: string;
  // 施設スコープ。API 側で必須。
  facilityId: string;
  // コーチ個別相談は受講者単位。複数の受講者がいる場合も曖昧に推測しない。
  subjectStudentId?: string;
}

export interface ChatMessageReaction {
  id: string;
  messageId: string;
  userId: string;
  userType: UserType;
  emoji: string;
  createdAt: string;
}
